import { Component, Inject, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { InitApp, SetDisabledCaptcha, SetRefreshRequiredStatus } from './core/store/app/app.actions';
import { AppState } from './core/store/app/app.state';
import { EnvService, SentryService, VersionUpdatesService } from './core/services';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter, map, Observable, pairwise } from 'rxjs';
import { PatientState } from './core/store/patient/patient.state';
import { Patient } from './core/models';
import { UserState } from './core/store/user/user.state';
import { Gtag } from 'angular-gtag';
import { pick } from 'lodash-es';

const project = require('package.json');

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  @Select(AppState.isRefreshByModalRequired) isRefreshByModalRequired$!: Observable<boolean>;
  @Select(PatientState.patient) patient$!: Observable<Patient | null>;
  @Select(UserState.isAgentUser) isAgentUser$!: Observable<boolean>;

  showVersion: boolean = !this.envService.isProduction();
  env: string = this.envService.environment;
  versionNo: string = project.version;

  // gtag instantiation is needed to start analytics
  constructor(
    private gtag: Gtag,
    private envService: EnvService,
    private sentryService: SentryService,
    private store: Store,
    private titleService: Title,
    private router: Router,
    private route: ActivatedRoute,
    private versionUpdatesService: VersionUpdatesService
  ) {}

  ngOnInit(): void {
    this.sentryService.init();
    const appTitle = this.titleService.getTitle();
    this.versionUpdatesService.initVersionCheck();

    const welcomeToken = window.location.search.includes('welcome-token');
    const resetPasswordToken = window.location.search.includes('reset-password-token');

    if (welcomeToken || resetPasswordToken) {
      this.store.dispatch(new InitApp(null, true));
    } else {
      this.store.dispatch(new InitApp(null));
    }

    this.route.queryParams.pipe(pairwise()).subscribe(([previousQueryParams, nextQueryParams]) => {
      const disableCaptchaKey = 'disableCaptcha';
      const otpKey = 'otp';
      const pqp = pick(previousQueryParams, [disableCaptchaKey, otpKey]);
      const nqp = pick(nextQueryParams, [disableCaptchaKey, otpKey]);

      if (pqp[disableCaptchaKey] !== nqp[disableCaptchaKey]) {
        this.store.dispatch(
          new SetDisabledCaptcha(!this.envService.isProduction() && parseInt(nqp[disableCaptchaKey]) === 1)
        );
      }

      if (pqp[otpKey] !== nqp[otpKey]) {
        this.store.dispatch(new InitApp(nqp[otpKey]));
      }
    });

    this.router.events.subscribe((e) => {
      if (e instanceof NavigationStart && e.navigationTrigger === 'popstate') {
        this.sentryService.addBreadcrumb({
          type: 'navigation',
          category: 'navigation',
          message: 'Browser back button or forward button clicked',
          data: {
            from: 'unknown',
            to: e.url,
          },
        });
      }
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let child = this.route.firstChild;

          while (child?.firstChild) {
            child = child.firstChild;
          }

          if (child?.snapshot.data['title']) {
            return `${appTitle} | ${child.snapshot.data['title']}`;
          }

          return appTitle;
        })
      )
      .subscribe((ttl: string) => {
        this.titleService.setTitle(ttl);
      });
  }

  versionUpdatesCopy() {
    if (!this.versionUpdatesService.versionUpdates) return null;

    const { updateMessageTitle, updateMessage, updateActionLabel } = this.versionUpdatesService.versionUpdates;

    return {
      updateMessage,
      updateMessageTitle,
      updateActionLabel,
    };
  }

  refreshPage() {
    this.store.dispatch(new SetRefreshRequiredStatus(false)).subscribe(() => {
      window.open('./', '_self');
    });
  }
}
